'use client';

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

const LS_KEY = 'wisprs_docs_lang';

export interface MultiLangTab {
  label: string;
  lang: string;
  html: string;
  code: string;
}

export function MultiLangCodeBlockClient({ tabs }: { tabs: MultiLangTab[] }) {
  const labels = tabs.map((t) => t.label);
  const [active, setActive] = useState<string>(labels[0]);
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Restore persisted language preference after hydration
  useEffect(() => {
    setMounted(true);

    try {
      const saved = localStorage.getItem(LS_KEY);

      if (saved && labels.includes(saved)) setActive(saved);
    } catch {
      // localStorage unavailable (SSR, private browsing)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function switchTab(label: string) {
    setActive(label);

    try {
      localStorage.setItem(LS_KEY, label);
    } catch {
      // ignore
    }
  }

  const current = tabs.find((t) => t.label === active) ?? tabs[0];

  function copy() {
    navigator.clipboard.writeText(current.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Suppress tab bar flash before localStorage resolves
  const resolvedActive = mounted ? active : labels[0];

  return (
    <div className="group relative my-5 overflow-hidden rounded-xl border border-[#2A2A2A] bg-[#111111]">
      {/* Header bar — traffic lights + language tabs + copy */}
      <div className="flex items-center justify-between border-b border-[#2A2A2A] bg-[#0D0D0D] pr-3">
        <div className="flex items-center gap-0">
          {/* Traffic lights */}
          <div className="flex gap-1.5 px-4 py-2.5">
            <div className="h-3 w-3 rounded-full bg-[#FF5F57]" />
            <div className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
            <div className="h-3 w-3 rounded-full bg-[#28C840]" />
          </div>
          {/* Language tabs */}
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.label}
                onClick={() => switchTab(tab.label)}
                className={cn(
                  'px-3.5 py-2.5 text-xs font-medium transition-colors',
                  resolvedActive === tab.label
                    ? 'border-b border-[rgb(var(--primary))] -mb-px text-[rgb(var(--foreground))]'
                    : 'text-[#6B7280] hover:text-[#A1A1AA]'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Copy button */}
        <button
          onClick={copy}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs text-[#6B7280] transition-all hover:bg-[#1A1A1A] hover:text-[#A1A1AA]"
        >
          {copied ? (
            <>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#28C840" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-[#28C840]">Copied!</span>
            </>
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <rect x="4" y="1" width="7" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 4v7h7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              Copy
            </>
          )}
        </button>
      </div>

      {/* Code panel — all tabs rendered, inactive ones hidden to avoid re-highlight flicker */}
      {tabs.map((tab) => (
        <div
          key={tab.label}
          hidden={tab.label !== (mounted ? active : labels[0])}
          className="overflow-x-auto p-5 text-[0.8125rem] leading-[1.7] [&_code]:block [&_code]:whitespace-pre [&_.line]:block"
          dangerouslySetInnerHTML={{ __html: tab.html }}
        />
      ))}
    </div>
  );
}
